import { getTypeformProps } from '@react/cta/helpers/getEventProps';
import TypeformWizard from '@react/cta/TypeformWizard/Wizard';
import Video from '@react/shared/components/Video';
import { DZComponentsNames } from '@sly/core/constants/dynamicZone';
import { useMemo } from 'react';
import DynamicAdvisors from 'sly/hubPage/components/HubDynamicContent/DynamicAdvisors';
import DynamicCommunity from 'sly/hubPage/components/HubDynamicContent/DynamicCommunity';
import DynamicEditor from 'sly/hubPage/components/HubDynamicContent/DynamicEditor';
import DynamicFaq from 'sly/hubPage/components/HubDynamicContent/DynamicFaq';
import DynamicImage from 'sly/hubPage/components/HubDynamicContent/DynamicImage';
import DynamicItemWithIcon from 'sly/hubPage/components/HubDynamicContent/DynamicItemWithIcon';
import DynamicLink from 'sly/hubPage/components/HubDynamicContent/DynamicLink';
import DynamicLinksBlock from 'sly/hubPage/components/HubDynamicContent/DynamicLinksBlock';
import DynamicListTwoColumns from 'sly/hubPage/components/HubDynamicContent/DynamicListTwoColumns';
import DynamicListWithIcons from 'sly/hubPage/components/HubDynamicContent/DynamicListWithIcons';
import DynamicQuote from 'sly/hubPage/components/HubDynamicContent/DynamicQuote';
import DynamicSearch from 'sly/hubPage/components/HubDynamicContent/DynamicSearch';
import DynamicSubtitle from 'sly/hubPage/components/HubDynamicContent/DynamicSubtitle';
import { HubDynamicContentProps } from 'sly/hubPage/components/HubDynamicContent/HubDynamicContent.types';
import InfoBlock from 'sly/hubPage/components/HubDynamicContent/InfoBlock';
import TableOfContents from 'sly/hubPage/components/HubDynamicContent/TableOfContents';
import { Content } from 'sly/hubPage/components/HubPage/HubPage.types';
import IframeContentComponent from 'sly/resourceCenter/components/IframeContent';
import Heading from 'sly/system/Heading';

import DynamicCarePlans from './DynamicCarePlans';
import DynamicFaqBlock from './DynamicFaqBlock';
import ExploreByEntity from './ExploreByEntity';
import ListWithImage from './ListWithImage';
import AvatarListWithImage from './ListWithImage/avatar';
import ThreeColumnValue from './ThreeColumnValue';

const isSubtitle = ({ __component }: Content) =>
  __component?.includes('subtitle');

export default function HubDynamicContent({
  content: data,
  isGeoGuidePage = false,
  isMarketingPage = false,
}: HubDynamicContentProps) {
  const content = useMemo(() => {
    // We don't need to worry about it being undefined
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return data?.map((item) => {
      if (isSubtitle(item)) {
        const htmlId = item.value
          .toLowerCase()
          .replace(/[^a-z\d-\s]+/g, '')
          .replace(/\s/g, '-');
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return { htmlId, ...item };
      }
      return item;
    });
  }, [data]);

  if (!data) {
    return null;
  }

  return (
    <div
      className='mt-l md:mt-xl'
      data-tp-id='gen-div-2c3cc8e1-2cfe-4c94-ae40-76e7ed8d5546'
    >
      <TableOfContents content={content} className='mb-xxl' />
      <div
        className='flex w-full flex-col flex-wrap items-center'
        data-tp-id='gen-div-ed6cc9f0-3da5-44aa-8282-1232d81f48d6'
      >
        {content?.map(({ __component, ...rest }: Content, index) => {
          const componentName = __component.split('.')[1];
          switch (componentName) {
            case DZComponentsNames.typeform:
              return (
                <div className='mb-20 min-h-[500px] w-[calc(100%-2rem)] md:mb-m md:w-[var(--col6)] lg:w-[var(--col8)]'>
                  <Heading font='font-t-m'>{rest?.title}</Heading>
                  <Heading font='font-b-m' className='mb-4'>
                    {rest?.description}
                  </Heading>
                  <div className='h-[500px]'>
                    <TypeformWizard
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      // @ts-ignore
                      {...getTypeformProps({
                        name: 'embeddedTypeform',
                        nonInteraction: 1,
                      })}
                      isConversionForm={false}
                      //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      wizardType='WIDGET'
                      formId={rest?.formID || ''}
                    />
                  </div>
                </div>
              );
            case DZComponentsNames.search:
              return (
                <DynamicSearch
                  key={`search${rest.id}`}
                  title={rest.title}
                  toc={rest.toc?.replace(/_/g, '-')}
                  placeholder={rest.placeholder}
                  include={rest.include}
                  disableGoogleSearch={rest.disableGoogleSearch}
                  redirectLocation={rest.redirectLocation}
                />
              );
            case DZComponentsNames.editor:
              return (
                <DynamicEditor
                  key={`editor${rest.id}`}
                  content={rest.content}
                  isGeoGuidePage={isGeoGuidePage}
                />
              );
            case DZComponentsNames.subtitle:
              return (
                <DynamicSubtitle
                  value={rest.value}
                  hrefId={rest.htmlId}
                  key={`subtitle${rest.id}`}
                />
              );
            case DZComponentsNames.listInTwoColumns:
              return (
                <DynamicListTwoColumns
                  key={`listInTwoColumns${rest.id}`}
                  listInTwoColumnsValue={rest.listInTwoColumnsValue}
                />
              );
            case DZComponentsNames.quote:
              return (
                <DynamicQuote
                  key={`quote${rest.id}`}
                  description={rest.description}
                  title={rest.title}
                />
              );
            case DZComponentsNames.faq:
              return (
                <DynamicFaq
                  key={`faq${rest.id}`}
                  title={rest.title}
                  description={rest.description}
                  withMarginBottom={
                    !content[index + 1]?.__component.includes(
                      DZComponentsNames.faq
                    )
                  }
                  withMarginTop={
                    !content[index - 1]?.__component.includes(
                      DZComponentsNames.faq
                    )
                  }
                />
              );
            case DZComponentsNames.faqBlock:
              return (
                <DynamicFaqBlock
                  key={`faqBlock${rest.id}`}
                  faqs={[{ __component, ...rest }]}
                />
              );
            case DZComponentsNames.communitiesList:
              return (
                <ExploreByEntity
                  key={`communitiesList${rest.id}`}
                  entity='communities'
                  title={rest.title}
                  tabList={rest.tabList}
                />
              );
            case DZComponentsNames.agentsList:
              return (
                <ExploreByEntity
                  key={`agentsList${rest.id}`}
                  entity='agents'
                  title={rest.title}
                  tabList={rest.tabList}
                />
              );
            case DZComponentsNames.link:
              return (
                <DynamicLink
                  key={`link${rest.id}`}
                  to={rest.to}
                  description={rest.description}
                  title={rest.title}
                />
              );
            case DZComponentsNames.image:
              return (
                <DynamicImage
                  image={rest.image}
                  key={`image${rest.id}`}
                  size={rest.size}
                />
              );
            case DZComponentsNames.listWithIcons:
              return (
                <DynamicListWithIcons
                  key={`listWithIcons${rest.id}`}
                  value={rest.value}
                />
              );
            case DZComponentsNames.itemWithIcon:
              return (
                <DynamicItemWithIcon
                  key={`itemWithIcon${rest.id}`}
                  value={rest.value}
                  icon={rest.icon}
                />
              );
            case DZComponentsNames.community:
              return (
                <DynamicCommunity
                  key={`community${rest.id}`}
                  data={rest.communities}
                />
              );
            case DZComponentsNames.advisors:
              return (
                <DynamicAdvisors
                  key={`advisors${rest.id}`}
                  advisors={rest.advisors}
                />
              );
            case DZComponentsNames.linksBlock:
              return (
                <DynamicLinksBlock
                  key={`linksBlock${rest.id}`}
                  title={rest.title}
                  description={rest.description}
                  links={rest.link}
                />
              );
            case DZComponentsNames.infoBlock:
              return (
                <InfoBlock
                  key={`infoBlock${rest.id}`}
                  infoBlockList={[{ __component, ...rest }]}
                />
              );
            case DZComponentsNames.listWithImg:
              return rest?.avatar ? (
                <AvatarListWithImage
                  getListWithImg={{ __component, ...rest }}
                  key={`listWithImg${rest.id}`}
                />
              ) : (
                <ListWithImage
                  getListWithImg={{ __component, ...rest }}
                  key={`listWithImg${rest.id}`}
                />
              );
            case DZComponentsNames.threeColumnValue:
              return (
                <ThreeColumnValue
                  getListWithIcons={[{ __component, ...rest }]}
                  key={`infoBlock${rest.id}`}
                />
              );
            case DZComponentsNames.video:
            case DZComponentsNames.youTubeVideoLink:
              return (
                <Video
                  title={rest.title}
                  url={rest.src || rest?.video?.url}
                  transcript={rest.transcript}
                  isVideo={componentName === DZComponentsNames.video}
                  key={`video${rest.id}`}
                  iframe={componentName === DZComponentsNames.youTubeVideoLink}
                  info={{
                    duration: rest.duration,
                    publishedAt: rest.publishedDate,
                  }}
                />
              );
            case DZComponentsNames.iframeContent:
              return (
                <IframeContentComponent
                  iframeContent={{
                    enable: rest.enable,
                    iframeCode: rest.iframeCode,
                    title: rest.title,
                    id: rest.id,
                  }}
                  isMarketingPage={isMarketingPage}
                />
              );
            case DZComponentsNames.carePlans:
              return <DynamicCarePlans />;
          }
        })}
      </div>
    </div>
  );
}
